import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { extractErrorMessage } from "@/shared/utils";
import { useCreateTaskMutation, useUpdateTaskMutation } from "../api";
import { taskFormSchema, type TaskFormValues } from "../validation";
import type { Assignee, Task, TaskListItem, TaskStatus } from "../types";

interface UseTaskFormProps {
  projectId: number;
  defaultStatus: TaskStatus | null;
  onSuccess: () => void;
  mode: "create" | "edit";
  task?: Task | null;
  assignees: Assignee[];
  kpiTasks: TaskListItem[];
  forceKpi?: boolean;
}

export function useTaskForm({
  projectId,
  defaultStatus,
  onSuccess,
  mode,
  task,
  assignees,
  kpiTasks,
  forceKpi = false,
}: UseTaskFormProps) {
  const { t } = useTranslation("TasksPage");
  const [errorPrompt, setErrorPrompt] = useState<{ title: string; text: string } | null>(null);

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(taskFormSchema),
    defaultValues: {
      title: "",
      description: "",
      status: defaultStatus ?? undefined,
      priority: "normal",
      due_date: "",
      tags: [],
      assigned_to: null,
      is_kpi: false,
      parent_task: null,
      requires_fsm_report: false,
    },
  });

  const { reset } = form;

  const [createTask, { isLoading: isCreating }] = useCreateTaskMutation();
  const [updateTask, { isLoading: isUpdating }] = useUpdateTaskMutation();

  useEffect(() => {
    if (mode === "edit" && task) {
      reset({
        title: task.title,
        description: task.description || "",
        status: task.status,
        priority: task.priority,
        due_date: task.due_date || "",
        tags: task.tags || [],
        assigned_to: task.assigned_to,
        is_kpi: task.parent_task !== null,
        parent_task: task.parent_task,
        requires_fsm_report: task.requires_fsm_report ?? false,
      });
    } else if (mode === "create") {
      reset({
        title: "",
        description: "",
        status: defaultStatus ?? undefined,
        priority: "normal",
        due_date: "",
        tags: [],
        assigned_to: null,
        is_kpi: false,
        parent_task: null,
        requires_fsm_report: false,
      });
    }
  }, [mode, task, reset, defaultStatus]);

  async function handleSubmit(values: TaskFormValues) {
    setErrorPrompt(null);
    try {
      const taskType = forceKpi
        ? "kpi" as const
        : values.parent_task
          ? "first_level_task" as const
          : "task" as const;

      if (mode === "create") {
        await createTask({
          projectId,
          data: {
            title: values.title,
            description: values.description || undefined,
            status: values.status,
            priority: values.priority,
            due_date: values.due_date || null,
            assigned_to: values.assigned_to,
            tags: values.tags,
            task_type: taskType,
            parent_task: values.parent_task ?? null,
            requires_fsm_report: values.requires_fsm_report,
          },
        }).unwrap();
      } else if (task) {
        await updateTask({
          projectId,
          id: task.id,
          data: {
            title: values.title,
            description: values.description || undefined,
            status: values.status,
            priority: values.priority,
            due_date: values.due_date || null,
            assigned_to: values.assigned_to,
            tags: values.tags,
            task_type: taskType,
            parent_task: values.parent_task ?? null,
            requires_fsm_report: values.requires_fsm_report,
          },
        }).unwrap();
      }

      reset({
        title: "",
        description: "",
        status: defaultStatus ?? undefined,
        priority: "normal",
        due_date: "",
        tags: [],
        assigned_to: null,
        is_kpi: false,
        parent_task: null,
        requires_fsm_report: false,
      });
      onSuccess();
    } catch (error) {
      const errorKey =
        mode === "create" ? "toast.taskCreateError" : "toast.taskUpdateError";
      const titleKey =
        mode === "create" ? "prompt.taskCreateErrorTitle" : "prompt.taskUpdateErrorTitle";
      setErrorPrompt({
        title: t(titleKey),
        text: extractErrorMessage(error, errorKey, t),
      });
    }
  }

  function handleCloseErrorPrompt() {
    setErrorPrompt(null);
  }

  return {
    register: form.register,
    watch: form.watch,
    setValue: form.setValue,
    errors: form.formState.errors,
    assignees,
    kpiTasks,
    isSubmitting: isCreating || isUpdating,
    errorPrompt,
    handleCloseErrorPrompt,
    handleSubmit: form.handleSubmit(handleSubmit),
  };
}
